import { AfterViewChecked, Directive, ElementRef, EventEmitter, forwardRef, Input, NgZone, OnInit, Output, Renderer2 } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import PlaceResult = google.maps.places.PlaceResult;
import AutocompleteOptions = google.maps.places.AutocompleteOptions;
import { ContactAddress } from 'app/interfaces/google-maps-autocomplete.inerface';
import { Loader } from '@googlemaps/js-api-loader';
import { environment as env } from '../../environments/environment';

@Directive({
    selector: '[googleMapsAutocomplete]',
    /* eslint-disable @angular-eslint/no-host-metadata-property */
    host: {
        '(blur)': 'onTouched()',
        '(keyup)': 'onChange($event.target.value)'
    },
    /* eslint-enable @angular-eslint/no-host-metadata-property */
    exportAs: 'googleMapsAutocomplete',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => GoogleMapsAutocompleteDirective),
            multi: true
        }
    ]
})

export class GoogleMapsAutocompleteDirective implements OnInit, AfterViewChecked, ControlValueAccessor {
    @Output()
    autocompleteSelected: EventEmitter<{ result: PlaceResult; address: ContactAddress }> = new EventEmitter<{ result: PlaceResult; address: ContactAddress }>();
    @Input() canTruncate: boolean = false;

    autoCompleteOptions: AutocompleteOptions = {};

    constructor(
        private elemRef: ElementRef,
        private ngZone: NgZone,
        private _renderer: Renderer2) {
    }

    ngAfterViewChecked(): void {
        this._renderer.setProperty(this.elemRef.nativeElement, 'autocomplete', 'off');
    }

    onChange: any = (_: any) => { };
    onTouched: any = () => { };

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this._renderer.setProperty(this.elemRef.nativeElement, 'disabled', isDisabled);
    }

    writeValue(value: any): void {
        const normalizedValue = value == null ? '' : value;
        this._renderer.setProperty(this.elemRef.nativeElement, 'value', normalizedValue);
    }

    ngOnInit(): void {
        this.initGoogleMapsAutocomplete();
    }

    public initGoogleMapsAutocomplete(): void {
        const loader = new Loader({
        apiKey: env.googleMapKey,
        libraries: ['places', 'geometry', 'drawing']
      }).load()
            .then(() => {
                const autocomplete = new google.maps.places.Autocomplete(this.elemRef.nativeElement, this.autoCompleteOptions);
                autocomplete.addListener('place_changed', () => {
                    this.ngZone.run(() => {
                        // get the place result
                        const place: PlaceResult = autocomplete.getPlace();
                        const eleValue: string = (this.elemRef.nativeElement as HTMLInputElement)?.value;
                        const splittedValue: string[] = eleValue.split(',');
                        const contactAddress: ContactAddress = {
                            geoLocation: { latitude: -1, longitude: -1 },
                        };
                        let spilceStartIndex: number = 0;
                        contactAddress.street = eleValue;

                        if (place?.geometry?.location) {
                            contactAddress.geoLocation.latitude = place.geometry.location.lat();
                            contactAddress.geoLocation.longitude = place.geometry.location.lng();
                        }

                        place?.address_components?.forEach((value: any) => {
                            if (value?.types?.indexOf('postal_code') > -1) {
                                contactAddress.zipcode = value.long_name;
                            }
                            if (value?.types?.indexOf('locality') > -1 || value?.types?.indexOf('neighborhood') > -1) {
                                contactAddress.city = value.long_name;
                                if (this.canTruncate) {
                                    splittedValue.find((val: string, sIndex: number) => {
                                        if (val.trim() === value.long_name) {
                                            spilceStartIndex = sIndex;
                                        }
                                    });
                                }
                            }
                            if (value?.types?.indexOf('administrative_area_level_1') > -1) {
                                contactAddress.state = value.long_name;
                            }
                            if (value?.types?.indexOf('country') > -1) {
                                contactAddress.country = {
                                    value: value.short_name,
                                    label: value.long_name
                                };
                            }
                        });

                        if (this.canTruncate && spilceStartIndex > 0) {
                            splittedValue.splice(spilceStartIndex, splittedValue.length);
                            contactAddress.street = splittedValue.join(',');
                            this._renderer.setProperty(this.elemRef.nativeElement, 'value', contactAddress.street);
                        }
                        this.autocompleteSelected.emit({ result: place, address: contactAddress });
                    });
                });
            })
            .catch((err) => { console.log(err); });
    }
}
